import { Box, Button } from "@mui/joy";
import useDetectIOS from "hooks/useDetectIOS";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { loginHintAtom } from "recoil/loginHintAtom";
import apple_logo from "static/image/common/apple_logo.png";
import google_logo from "static/image/common/google_logo.png";
import kakao_logo from "static/image/common/kakao_logo.png";
import { useTranslation } from "react-i18next";
import { theme } from "styles/theme";

export default function LoginButtonTemplate({
  loginType,
  action,
  isLoggedIn,
  widthPx,
  heightPx,
}: {
  loginType: "apple" | "google" | "kakao";
  action: () => void;
  isLoggedIn: boolean;
  widthPx?: number | string;
  heightPx?: number | string;
}) {
  const user = useRecoilValue(userAtom);
  const loginHint = useRecoilValue(loginHintAtom);
  // OneLink로 전달된 최근 로그인 수단과 일치하는 버튼에만 "최근 로그인" 라벨 표시
  const isRecentLogin = loginHint === loginType;
  const disabled = isLoggedIn;
  const { isIOS } = useDetectIOS();

  const { t: t_ui } = useTranslation("translation", {
    keyPrefix: "ui",
  });

  const loginDataByType: {
    [key in "apple" | "google" | "kakao"]: {
      logo?: string;
      buttonText: string;
      backgroundColor: string;
      color: string;
      borderColor?: string;
    };
  } = {
    apple: {
      logo: apple_logo,
      buttonText: t_ui("로그인", { type: "Apple" }),
      backgroundColor: theme.vars.palette.Surface.Forward,
      color: theme.vars.palette.Content.Strong,
      borderColor: "none",
    },
    google: {
      logo: google_logo,
      buttonText: t_ui("로그인", { type: "Google" }),
      backgroundColor: theme.vars.palette.Surface.Forward,
      color: theme.vars.palette.Content.Strong,
      borderColor: "none",
    },
    kakao: {
      logo: kakao_logo,
      buttonText: t_ui("로그인", { type: "Kakao" }),
      backgroundColor: "#FEE500",
      color: theme.vars.palette.Content.Strong,
      borderColor: "none",
    },
  };

  const currentLoginData = loginDataByType[loginType];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isRecentLogin && (
        <Box
          sx={{
            position: "absolute",
            top: "-9px",
            right: "8px",
            zIndex: 1,
            px: "8px",
            py: "2px",
            borderRadius: "999px",
            // Figma node 8267-19107: 다크 네이비 pill + 흰 글자, 버튼 우상단 모서리
            backgroundColor: theme.vars.palette.Content.Strong,
            color: "#fff",
            fontSize: "11px",
            fontWeight: 600,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {t_ui("최근 로그인")}
        </Box>
      )}
      <Button
        onClick={!disabled ? action : () => {}}
      variant={"Plain"}
      color={"neutral"}
      size="md"
      sx={{
        backgroundColor: currentLoginData.backgroundColor,
        color: currentLoginData.color,
        width: widthPx,
        "& > .MuiButton-startDecorator": {
          marginInlineEnd: "6px",
        },
        "@media (min-width: 1024px)": {
          "&:hover:not(.Mui-disabled)": {
            backgroundColor: currentLoginData.backgroundColor,
            color: currentLoginData.color,
            opacity: 0.6,
          },
        },
        "@media (max-width: 1024px)": {
          "&:hover": {
            // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
            // ios : 버튼 클릭 후 touchend 해도 hover
            backgroundColor: currentLoginData.backgroundColor,
            color: currentLoginData.color,
          },
        },
        "&:focus": {
          backgroundColor: currentLoginData.backgroundColor,
          color: currentLoginData.color,
        },
        "&:active": {
          backgroundColor: currentLoginData.backgroundColor,
          color: currentLoginData.color,
        },
      }}
      startDecorator={
        <img
          src={currentLoginData.logo}
          alt={"empty"}
          style={{
            width: "20px",
            height: "20px",
            objectFit: "contain",
          }}
        />
      }
    >
      {currentLoginData.buttonText}
    </Button>
    </Box>
  );
}